import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CompetenceMonthField from './CompetenceMonthField';
import TcPagination from './TcPagination';
import StatusChip from './ui/StatusChip';
import { fetchObligationPage } from '../utils/monthlyInvoiceApi';
import {
  applySituationFilter,
  currentCompetenceBrazil,
  describeMonthlyRow,
  HistoryTarget,
  MONTHLY_PAGE_LIMIT,
  monthlyInvoiceErrorMessage,
  noteReviewTarget,
  ObligationPage,
  overviewMessage,
  SITUATION_FILTER_ENABLED,
  SITUATION_FILTER_PENDING,
} from '../utils/monthlyInvoiceView';

const TONE = {
  not_obligated: 'info',
  not_submitted: 'neutral',
  awaiting_review: 'warning',
  rejected: 'danger',
  approved: 'success',
} as const;

interface MonthlyInvoiceControlProps {
  loadPage?: (competence: string, page: number, limit: number) => Promise<ObligationPage>;
  onOpenHistory?: (target: HistoryTarget) => void;
}

const MonthlyInvoiceControl: React.FC<MonthlyInvoiceControlProps> = ({
  loadPage = fetchObligationPage,
  onOpenHistory,
}) => {
  const [competence, setCompetence] = useState(currentCompetenceBrazil);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ObligationPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void loadPage(competence, page, MONTHLY_PAGE_LIMIT)
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((cause) => {
        if (active) {
          setResult(null);
          setError(monthlyInvoiceErrorMessage(cause));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [competence, page, loadPage]);

  const rows = result ? applySituationFilter(result).map(describeMonthlyRow) : [];
  const message = result ? overviewMessage(result) : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 800 }}>
        Controle mensal de notas fiscais
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Colaboradores com obrigação documental na competência. Aprovação da nota não autoriza pagamento.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <CompetenceMonthField
          label="Competência"
          value={competence}
          onChange={(next) => {
            setPage(1);
            setCompetence(next);
          }}
          minWidth={240}
        />
        <TextField
          label="Situação documental"
          value={SITUATION_FILTER_ENABLED ? '' : 'Indisponível nesta versão'}
          disabled
          helperText={SITUATION_FILTER_PENDING}
          sx={{ minWidth: 280 }}
        />
      </Stack>
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? <Typography color="text.secondary">Carregando controle mensal…</Typography> : null}
      {!loading && message ? <Typography color="text.secondary">{message}</Typography> : null}
      {rows.length > 0 ? (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Colaborador</TableCell>
              <TableCell>Vínculo</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Notas</TableCell>
              <TableCell>Histórico</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.userId}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.contract}</TableCell>
                <TableCell>
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <StatusChip label={row.situation} tone={TONE[row.situationCode]} />
                    {row.documentary ? (
                      <Typography variant="caption">{row.documentary}</Typography>
                    ) : null}
                    {row.multipleLabel ? <StatusChip label={row.multipleLabel} tone="info" /> : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.total}</Typography>
                  {row.countsLabel ? (
                    <Typography variant="caption" color="text.secondary">
                      {row.countsLabel}
                    </Typography>
                  ) : null}
                  {row.notes.map((note) => (
                    <Typography key={note.id} variant="caption" sx={{ display: 'block' }}>
                      {note.number} · {note.status}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onOpenHistory?.(row.history)}>
                    Abrir revisão
                  </Button>
                  {row.notes.map((note) => (
                    <Button
                      key={note.id}
                      size="small"
                      onClick={() =>
                        onOpenHistory?.(
                          noteReviewTarget(
                            {
                              userId: row.userId,
                              userName: row.name,
                              competence: row.history.competence,
                            },
                            note,
                          ),
                        )
                      }
                    >
                      Abrir nota
                    </Button>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      <TcPagination
        page={result?.page ?? page}
        totalPages={result?.totalPages ?? 0}
        total={result?.total ?? 0}
        pageSize={result?.limit ?? MONTHLY_PAGE_LIMIT}
        onPageChange={setPage}
        disabled={loading}
        itemLabel="colaboradores"
      />
    </Box>
  );
};

export default MonthlyInvoiceControl;
